import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { STUDY_INSIGHTS_EDIT_GOALS } from '../constants';
import { editGoals } from '../services/edit-goals.service';
import { EditedGoal, EditGoal } from '../components/CollectionAndSequencing/Overview/SequenceGoal/SequenceGoal.types';

export interface EditGoalsContext {
  prevoiusGoals: EditGoal;
}

export type EditGoalsVariables = EditGoal;

function useMutationEditGoals(
  onSuccess: (data: any, variables: EditGoalsVariables, context: EditGoalsContext | undefined) => void,
  onError?: () => void,
): UseMutationResult<string, unknown> {
  const queryClient = useQueryClient();

  const mutationFn = async (editedGoals: EditedGoal): Promise<any> => {
    await editGoals(editedGoals);
  };

  const onMutateFn = async (editedGoals: EditGoal): Promise<any> => {
    // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
    await queryClient.cancelQueries({ queryKey: [STUDY_INSIGHTS_EDIT_GOALS] });

    const previousUsers = queryClient.getQueryData([STUDY_INSIGHTS_EDIT_GOALS]) as EditGoal;

    // Optimistically update to the new value
    queryClient.setQueryData([STUDY_INSIGHTS_EDIT_GOALS], editedGoals);

    // Return a context object with the snapshotted value
    return { previousUsers };
  };

  const onErrorFn = (_err: unknown, _id: string, context: EditGoalsContext | undefined): void => {
    if (context === undefined) {
      return;
    }
    if (context.prevoiusGoals !== undefined) {
      queryClient.setQueryData([STUDY_INSIGHTS_EDIT_GOALS], context.prevoiusGoals);
    }
    onError?.();
  };

  const onSettledFn = async (): Promise<void> => {
    setTimeout(() => {
      void queryClient.invalidateQueries([STUDY_INSIGHTS_EDIT_GOALS]);
    }, 2000);
  };

  return useMutation<string, unknown, any, any>([STUDY_INSIGHTS_EDIT_GOALS], mutationFn, {
    onMutate: onMutateFn,
    // If the mutation fails, use the context returned from onMutate to roll back
    onError: onErrorFn,
    // Always refetch after error or success:
    onSettled: onSettledFn,
    onSuccess,
  });
}

export default useMutationEditGoals;
